<template>
  <div class="inputTextWrapper" :class="{ error: !validity }">
    <LabelInput
      v-if="$slots.label || label"
      :label="label"
      :label-annotation="labelAnnotation"
      :html-for="uuid"
    >
      <template #label>
        <slot name="label" />
      </template>
    </LabelInput>
    <div class="grooverInnerWrap" :class="{ focus, showV2 }">
      <slot name="left-picto">
        <i
          v-if="picto"
          class="picto"
          :class="[picto, { pictoError: !validity }]"
        />
      </slot>
      <slot name="right-picto">
        <transition name="fade" mode="out-in">
          <i
            v-if="rightPictoIsDisplayed"
            class="pictoRight"
            :class="validatedPictoClass"
          />
        </transition>
      </slot>
      <input
        :id="uuid"
        :class="{
          disabled,
          softDisabled,
          hasPicto,
          rightPad,
          showInputBorder,
          noPadding,
          'tw-cursor-not-allowed': disabled,
        }"
        :type="type"
        :value="copy"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :maxlength="maxlength"
        @keyup="handleKeyup"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      />
    </div>
    <HeightExpand>
      <div
        v-if="!validity && !isValidating && ($slots.errormsg || errmsg)"
        class="errorHolder"
      >
        <VText cfg="sans/14/medium" class="tw-text-error">
          <slot v-if="$slots.errormsg" name="errormsg" />
          <template v-else>{{ errmsg }} </template>
        </VText>
      </div>
    </HeightExpand>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import HeightExpand from '~/components/transitions/heightExpand.vue'
import LabelInput from '~/components/ui/LabelInput.vue'
import VText from '~/components/ui/VText.vue'

import validationChildMixin from '~/mixins/validationChild'

import type { PropType } from 'vue'

export default defineComponent({
  components: {
    LabelInput,
    HeightExpand,
    VText,
  },
  mixins: [validationChildMixin],
  props: {
    modelValue: {
      type: String as PropType<string | null>,
      required: false,
      default: '',
    },
    type: {
      type: String as PropType<'text' | 'password'>,
      default: 'text',
    },
    label: {
      type: [String, Number],
      required: false,
    },
    labelAnnotation: {
      type: [String, Number],
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
      default() {
        return ''
      },
    },
    errmsg: {
      type: String,
      required: false,
      default() {
        return ''
      },
    },
    maxlength: {
      type: Number,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    softDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    displayRightCheck: {
      type: Boolean,
      default: true,
    },
    validatedPictoClass: {
      type: String,
      required: false,
      default: 'fas fa-check',
    },
    picto: {
      type: String,
      required: false,
      default: '',
    },
    startFocused: {
      type: Boolean,
      required: false,
      default: false,
    },
    showV2: {
      type: Boolean,
      required: false,
      default: false,
    },
    showInputBorder: {
      type: Boolean,
      required: false,
      default: true,
    },
    noPadding: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['update:modelValue', 'focus', 'blur', 'enter', 'keyup'],
  setup() {
    const uuid = useId()

    return {
      uuid,
    }
  },
  data() {
    return {
      focus: false,
      copy: '',
    }
  },
  computed: {
    isDisabled(): boolean {
      return this.disabled || this.softDisabled
    },
    rightPictoIsDisplayed(): boolean {
      return (
        this.validity &&
        (this.modelValue?.length ?? 0) > 0 &&
        !this.focus &&
        !this.isValidating &&
        this.pApplyValidity &&
        this.displayRightCheck
      )
    },
    hasPicto(): boolean {
      return this.picto.length > 0 || this.$slots['left-picto'] !== undefined
    },
    rightPad(): boolean {
      return (
        (this.displayRightCheck &&
          this.validity &&
          this.modelValue &&
          this.modelValue.length > 0) ||
        this.$slots['right-picto'] !== undefined
      )
    },
  },
  watch: {
    modelValue: 'handleUpdate',
    copy: 'handleChange',
  },
  mounted(): void {
    if (this.startFocused) document.getElementById(this.uuid)?.focus()

    this.handleChange(this.modelValue)
    this.copy = this.modelValue?.toString() ?? ''
  },
  methods: {
    handleKeyup(e: KeyboardEvent): void {
      if (e.key === 'Enter') this.$emit('enter', e)

      this.$emit('keyup', e)
    },
    handleBlur(e: Event): void {
      this.$nextTick(() => {
        this.focus = false
      })
      this.$emit('blur', e)
    },
    handleFocus(e: Event): void {
      this.focus = true
      this.$emit('focus', e)
    },
    handleChange(value: string | null): void {
      this.$emit('update:modelValue', value)
    },
    handleInput(e: Event): void {
      this.handleUpdate((e.target as HTMLInputElement).value)
    },
    handleUpdate(value: string | null): void {
      this.copy = value?.toString() ?? ''
      this.handleChange(value?.toString() ?? null)
    },
    makeInputLoseFocus(): void {
      const inputElement = document.getElementById(
        this.uuid.toString(),
      ) as HTMLInputElement
      inputElement.blur()
    },
  },
})
</script>

<style scoped lang="scss">
.grooverInnerWrap {
  @apply tw-relative tw-w-full;

  .focus > .picto {
    @apply tw-text-gray-500 tw-transition-colors tw-duration-100 tw-ease-in-out;
  }

  .disabled {
    @apply tw-bg-gray-100;
  }

  .softDisabled:not(.disabled) {
    @apply tw-bg-white;
  }

  .pictoError {
    color: theme('colors.error') !important;
  }

  :deep(.pictoRight) {
    @apply tw-absolute tw-top-0 tw-flex tw-h-full tw-items-center tw-justify-center tw-text-base;
    right: theme('spacing.4');
    background: linear-gradient(
      -45deg,
      theme('colors.blue-500'),
      theme('colors.green-500')
    );
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  :deep(.picto) {
    @apply tw-absolute tw-top-4 tw-ml-4 tw-h-4 tw-w-4 tw-text-base tw-text-gray-400;
  }

  input {
    @apply tw-h-[51px] tw-w-full tw-px-6 tw-py-4 tw-text-base tw-text-black tw-transition-colors tw-duration-100 tw-ease-in-out;
    outline: unset !important;

    &.noPadding {
      @apply tw-min-w-fit tw-p-0;
    }
    &.showInputBorder {
      @apply tw-rounded-xs tw-border tw-border-solid tw-border-gray-300;
      &:focus {
        @apply tw-border-gray-500;
      }
    }

    &::placeholder {
      @apply tw-h-12 tw-pt-[28px] tw-text-base tw-text-gray-400 tw-duration-150;
    }

    &:focus {
      @apply tw-border-gray-500 tw-transition-colors tw-duration-100 tw-ease-in-out;

      box-shadow: unset !important;
      outline: unset !important;

      &::placeholder {
        @apply tw-text-gray-400 tw-duration-100;
      }
    }

    &.hasPicto {
      @apply tw-pl-10;
    }

    &.rightPad {
      @apply tw-pr-10;
    }
  }
  &.showV2 {
    input {
      &::placeholder {
        @apply tw-text-sm tw-text-gray-500;
      }
      &:focus {
        border-color: theme('colors.black') !important;
      }
      &.hasPicto {
        @apply tw-h-10 tw-border tw-border-gray-300 tw-bg-white tw-py-3 tw-pl-[37px] tw-text-sm;
      }
    }
  }
}

.error input {
  border-color: theme('colors.error') !important;
  // @apply tw-border-error;
}

.errorHolder {
  @apply tw-mt-2;
}
</style>
